'use client';

import * as React from 'react';

type Theme = 'dark' | 'light' | 'system';

type ThemeProviderProps = {
	children: React.ReactNode;
	defaultTheme?: Theme;
	storageKey?: string;
};

type ThemeProviderState = {
	/** What the user chose, which may be `system`. */
	theme: Theme;
	/** What is actually on the screen right now - never `system`. */
	resolvedTheme: 'dark' | 'light';
	setTheme: (theme: Theme) => void;
};

import { THEME_STORAGE_KEY } from '@/lib/utils/theme-script.util';

export { THEME_STORAGE_KEY };

const initialState: ThemeProviderState = {
	theme: 'light',
	resolvedTheme: 'light',
	setTheme: () => null,
};

export const ThemeProviderContext = React.createContext<ThemeProviderState>(initialState);

const DARK_QUERY = '(prefers-color-scheme: dark)';
const subscribeNothing = () => () => {};
function subscribeSystem(onChange: () => void) {
	const media = window.matchMedia(DARK_QUERY);
	media.addEventListener('change', onChange);
	return () => media.removeEventListener('change', onChange);
}
const systemIsDark = () => window.matchMedia(DARK_QUERY).matches;

function readStored(storageKey: string, fallback: Theme): Theme {
	try {
		return (localStorage.getItem(storageKey) as Theme) || fallback;
	} catch {
		// Firefox with cookies blocked throws SecurityError on the localStorage PROPERTY ACCESS;
		// this runs above the router, so an unguarded throw here blanks the whole app.
		return fallback;
	}
}

/**
 * Applies the theme to the two hooks the stylesheet keys off.
 *
 * `data-theme` is what the HumAIn tokens use; `.dark` is what the components carried over
 * from the previous UI still read, and what Tailwind's `dark:` variant matches. Both are set
 * from one place so the two can never disagree.
 */
function applyTheme(resolved: 'dark' | 'light') {
	const root = window.document.documentElement;
	root.setAttribute('data-theme', resolved);
	root.classList.toggle('dark', resolved === 'dark');
	root.classList.toggle('light', resolved === 'light');
}

export function ThemeProvider({
	children,
	defaultTheme = 'light',
	storageKey = THEME_STORAGE_KEY,
	...props
}: ThemeProviderProps) {
	// Both reads go through useSyncExternalStore. A client-only app (Vite) gets the stored theme
	// and the OS setting on its very first render, as before. A server render, and the first
	// render while hydrating it, get the default instead - the server has no localStorage or
	// window - so the two agree, and React re-renders with the stored theme straight after.
	const stored = React.useSyncExternalStore(subscribeNothing, () => readStored(storageKey, defaultTheme), () => defaultTheme);
	const [chosen, setChosen] = React.useState<Theme | null>(null);
	const theme = chosen ?? stored;
	const osDark = React.useSyncExternalStore(subscribeSystem, systemIsDark, () => false);
	const resolvedTheme: 'dark' | 'light' = theme === 'system' ? (osDark ? 'dark' : 'light') : theme;

	React.useEffect(() => applyTheme(resolvedTheme), [resolvedTheme]);

	const value = React.useMemo(
		() => ({
			theme,
			resolvedTheme,
			setTheme: (next: Theme) => {
				// Storage can be blocked (private modes, cookie settings); the choice then lasts
				// for the session instead of throwing out of the click handler.
				try {
					localStorage.setItem(storageKey, next);
				} catch {
					/* not persisted */
				}
				setChosen(next);
			},
		}),
		[theme, resolvedTheme, storageKey],
	);

	return (
		<ThemeProviderContext.Provider {...props} value={value}>
			{children}
		</ThemeProviderContext.Provider>
	);
}
